import theme from "@shared/utils/themeColors";
import { format, isToday, isYesterday } from "date-fns";
import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { db } from "../../../config/firebase";
import { useSupport } from "../context/SupportContext";

/**
 * Component to display the list of support tickets for admins
 */
const SupportTicketList: React.FC = () => {
  const { tickets, loading, selectedTicketId, setSelectedTicketId } =
    useSupport();
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Format date for ticket display
  const formatTicketDate = (date: Date) => {
    if (isToday(date)) {
      return `Today, ${format(date, "h:mm a")}`;
    } else if (isYesterday(date)) {
      return `Yesterday, ${format(date, "h:mm a")}`;
    } else {
      return format(date, "MMM d");
    }
  };

  // Get message preview text
  const getMessagePreview = (ticket: any) => {
    if (!ticket.lastMessage) return "New conversation";

    if (ticket.lastMessage.type === "deleted") {
      return "Message deleted";
    } else if (ticket.lastMessage.type === "text") {
      return ticket.lastMessage.content.length > 30
        ? ticket.lastMessage.content.substring(0, 30) + "..."
        : ticket.lastMessage.content;
    } else {
      return `[${ticket.lastMessage.type.toUpperCase()}]`;
    }
  };
  
  // Fetch user profiles for all tickets to display real names
  useEffect(() => {
    if (!tickets.length) return;
    
    const fetchUserProfiles = async () => {
      const newUserNames: Record<string, string> = {};
      
      // Only fetch for users we don't already have
      const usersToFetch = tickets
        .filter(ticket => !userNames[ticket.userId] && ticket.userId)
        .map(ticket => ticket.userId);
      
      // Avoid duplicate fetches
      const uniqueUserIds = [...new Set(usersToFetch)];
      
      for (const userId of uniqueUserIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.profile && userData.profile.name) {
              newUserNames[userId] = userData.profile.name;
            }
          }
        } catch (error) {
          console.error(`Error fetching user profile for ${userId}:`, error);
        }
      }
      
      if (Object.keys(newUserNames).length > 0) {
        setUserNames(prevNames => ({
          ...prevNames,
          ...newUserNames
        }));
      }
    };
    
    fetchUserProfiles();
  }, [tickets, userNames]);

  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <div
          className={`animate-spin rounded-full h-5 w-5 border-b-2 ${theme.border.brand}`}
        ></div>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className={`p-4 text-center ${theme.text.secondary}`}>
        <p>No support tickets available</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {tickets.map((ticket) => (
        <button
          key={ticket.id}
          className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition duration-150 ${
            selectedTicketId === ticket.id ? "bg-gray-100 dark:bg-gray-800" : ""
          }`}
          onClick={() => setSelectedTicketId(ticket.id)}
        >
          <div className="flex justify-between items-start mb-1">
            <h3 className={`font-medium ${theme.text.primary}`}>
              {ticket.userName || userNames[ticket.userId] || "User " + ticket.userId.substring(0, 6)}
            </h3>
            <span className={`text-xs ${theme.text.muted}`}>
              {formatTicketDate(ticket.updatedAt)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <p className={`text-sm truncate ${theme.text.secondary}`}>
              {getMessagePreview(ticket)}
            </p>

            <div className="flex items-center space-x-2">
              {ticket.status === "closed" && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 ${theme.text.muted}`}
                >
                  Closed
                </span>
              )}
              {ticket.unreadAdminCount > 0 && (
                <span className="text-xs w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full">
                  {ticket.unreadAdminCount > 9 ? "9+" : ticket.unreadAdminCount}
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

export default SupportTicketList;
